import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiArrowDown } from 'react-icons/fi';
import AxiosUrl from '../AxiosUrl';

import styles from './Billing.module.css';
import PaymentConfirmModal from './BillingModal/PaymentConfirmModal';
import Features from './Features';

// Latin → Bangla digits for the launch-offer countdown.
const bn = (v) => String(v).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);
const pad2 = (n) => String(Math.max(0, n)).padStart(2, '0');

/**
 * Build the short, differentiator-focused highlight list shown on each pricing
 * card. Only the few things that differ plan-to-plan (plus the orders headline);
 * the full feature catalogue lives in the "Everything You Need to Succeed"
 * section below, so the cards stay clean and scannable.
 * Returns [{ text, included }] — included drives the green ✓ vs muted ✗.
 */
const buildHighlights = (pkg) => {
    const l = pkg.limits || {};
    const orders =
        l.orders === -1
            ? 'আনলিমিটেড অর্ডার'
            : `${(l.orders || 0).toLocaleString()} অর্ডার/মাস`;
    const products =
        l.products === -1
            ? 'আনলিমিটেড প্রোডাক্ট'
            : `${(l.products || 0).toLocaleString()} প্রোডাক্ট`;
    // Every plan advertises the SAME feature set (all included). The only
    // differences between plans are the orders/month, product count and the
    // support tier: Business gets priority phone support; the rest get ticket.
    const support =
        pkg.packageType === 'business'
            ? 'প্রায়োরিটি ফোন সাপোর্ট'
            : 'টিকেট সাপোর্ট';
    return [
        { text: orders, included: true },
        { text: products, included: true },
        { text: 'সুপার ফাস্ট ওয়েবসাইট', included: true },
        { text: 'অ্যাডভান্স রিপোর্ট', included: true },
        { text: 'ফেসবুক পিক্সেল + কনভার্সন API', included: true },
        { text: 'গুগল অ্যানালিটিক্স ও ট্যাগ ম্যানেজার', included: true },
        { text: 'বিকাশ পেমেন্ট গেটওয়ে', included: true },
        { text: support, included: true },
    ];
};

/** Short Bangla positioning line per plan. */
const taglineFor = (packageType) => {
    const map = {
        startup: 'নতুন ব্যবসা শুরু করার জন্য',
        smallBusiness: 'বাড়ন্ত ব্যবসার জন্য',
        business: 'দ্রুত স্কেল করার জন্য',
        enterprise: 'বড় এন্টারপ্রাইজের জন্য',
    };
    return map[packageType] || 'আপনার ব্যবসার জন্য';
};

/**
 * Helper function to convert package to display format
 */
const convertPackageFormat = (pkg) => {
    return {
        id: pkg._id,
        packageType: pkg.packageType,
        name: pkg.nameBangla || pkg.name,
        monthly: {
            price: pkg.pricing.monthly.price,
            displayPrice: pkg.pricing.monthly.displayPrice,
            period: 'মাস',
        },
        yearly: {
            price: pkg.pricing.yearly.price,
            displayPrice: pkg.pricing.yearly.displayPrice,
            period: 'বছর',
        },
        highlights: buildHighlights(pkg),
        tagline: taglineFor(pkg.packageType),
        colors: pkg.display?.colors || {
            header: 'rgb(66, 82, 206)',
            price: 'rgb(64, 78, 189)',
        },
        config: {
            visitor: pkg.limits.visitors,
            unlimited: pkg.limits.visitors === -1 && pkg.limits.products === -1 ? '1' : '0',
            product: pkg.limits.products,
        },
        badge: pkg.display?.badge?.text || (pkg.packageType === 'business' ? 'Premium' : 'Basic'),
        isPopular: pkg.packageType === 'business',
    };
};

function Billing() {
    const uid = localStorage.getItem('id');
    const navigate = useNavigate();
    const [open, setOpenModal] = useState(false);

    const [ActivePackage, setActivePackage] = useState();
    const [nextBilling, setNextBilling] = useState();
    const [Userpackage, setUserpackage] = useState();
    const [data, setData] = useState();
    const [packageName, setPackageName] = useState('');
    const [isMonthly, setIsMonthly] = useState(true); // Default to Monthly
    const [VisitorLeft, setVisitorLeft] = useState();
    const [Unlimited, setUnlimited] = useState();
    const [orderUsage, setOrderUsage] = useState(null);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [offerDeadline, setOfferDeadline] = useState(null);
    const [offerRemaining, setOfferRemaining] = useState(0);
    const [payErrorMsg, setPayErrorMsg] = useState('');

    const handleSwitchChange = () => {
        setIsMonthly((prevIsMonthly) => !prevIsMonthly);
    };

    // Show a payment error returned from the bKash callback as an inline banner
    // (a toast proved unreliable on this page; an inline message always renders).
    // The error is carried in the URL param ?payError=... — we KEEP it in the URL
    // (do not strip it); sessionStorage is a fallback. User dismisses via the ✕.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let msg = params.get('payError');
        if (!msg) {
            try {
                msg = sessionStorage.getItem('payError');
            } catch (e) {
                /* ignore */
            }
        }
        // sessionStorage is only a fallback carrier; clear it, but leave the URL
        // param in place so the error stays reflected in the address bar.
        try {
            sessionStorage.removeItem('payError');
        } catch (e) {
            /* ignore */
        }
        if (msg) setPayErrorMsg(msg);
    }, []);

    // Fetch subscription info
    useEffect(() => {
        setSubscriptionLoading(true);
        AxiosUrl.get(`/get-subscription/${uid}`)
            .then((res) => {
                const data = res.data.data || res.data;
                setActivePackage(data.ActivePackage);
                setUserpackage(data.package);
                setVisitorLeft(data.VisitorLeft);
                setUnlimited(data.Unlimited);
                setOrderUsage({
                    limit: data.OrderLimit,
                    count: data.OrderCount,
                    left: data.OrderLeft,
                    extraOrders: data.ExtraOrders || 0,
                    extraThisMonth: data.ExtraOrderChargeThisMonth || 0,
                    due: data.ExtraOrderDue || 0,
                    rate: data.ExtraOrderRate || 1,
                });
                const dateObject = new Date(data.nextBilling);

                const formattedDate = new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }).format(dateObject);
                setNextBilling(formattedDate);

                // Launch-offer banner: show only while the ৳1,000/yr offer is
                // still claimable for this (unpaid) user.
                if (data.offerActive && data.offerExpiresAt) {
                    setOfferDeadline(new Date(data.offerExpiresAt).getTime());
                } else {
                    setOfferDeadline(null);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setSubscriptionLoading(false);
            });
    }, [uid]);

    // Tick the launch-offer countdown.
    useEffect(() => {
        if (!offerDeadline) {
            setOfferRemaining(0);
            return;
        }
        const tick = () => {
            const left = offerDeadline - Date.now();
            setOfferRemaining(left);
            if (left <= 0) setOfferDeadline(null);
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [offerDeadline]);

    // Fetch billing packages
    useEffect(() => {
        AxiosUrl.get('/billing-packages')
            .then((res) => {
                if (res.data.success && res.data.data) {
                    const converted = res.data.data.map(convertPackageFormat);
                    setPackages(converted);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Failed to load packages:', error);
                setLoading(false);
            });
    }, []);

    const handlePackageSelect = (pkg) => {
        // FIX: Use new secure format - send packageType instead of price
        setData({
            packageType: pkg.packageType,
            billingPeriod: isMonthly ? 'monthly' : 'yearly',
            packageName: pkg.name,
        });
        setPackageName(pkg.name);
        setOpenModal(true);
    };

    const offerSecLeft = Math.max(0, Math.floor(offerRemaining / 1000));
    const offH = Math.floor(offerSecLeft / 3600);
    const offM = Math.floor((offerSecLeft % 3600) / 60);
    const offS = offerSecLeft % 60;
    const showOfferBanner = !!offerDeadline && offerRemaining > 0;

    return (
        <>
            <div className={styles['main-billing']}>
                {open && <PaymentConfirmModal data={data} packageName={packageName} setOpenModal={setOpenModal} />}

                {payErrorMsg && (
                    <div className={styles['pay-error-banner']} role="alert">
                        <span className={styles['pay-error-icon']} aria-hidden="true">
                            <FiX />
                        </span>
                        <span className={styles['pay-error-text']}>{payErrorMsg}</span>
                        <button
                            type="button"
                            className={styles['pay-error-close']}
                            onClick={() => setPayErrorMsg('')}
                            aria-label="বন্ধ করুন"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {showOfferBanner && (
                    <div
                        className={styles['offer-banner']}
                        onClick={() => navigate('/get-started')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') navigate('/get-started');
                        }}
                    >
                        <span className={styles['offer-banner-text']}>
                            🎉 লঞ্চ অফার — স্টার্টআপ ১ বছর মাত্র <strong>৳১,০০০</strong>
                        </span>
                        <span className={styles['offer-banner-timer']}>
                            ⏳ {bn(pad2(offH))}:{bn(pad2(offM))}:{bn(pad2(offS))}
                        </span>
                        <span className={styles['offer-banner-cta']}>এখনই নিন →</span>
                    </div>
                )}

                {/* Header Section */}
                <div className={styles['billing-header']}>
                    <h1 className={styles['billing-title']}>
                        Choose Your Plan
                    </h1>
                    <p className={styles['billing-subtitle']}>
                        Select the perfect plan for your business needs
                    </p>
                </div>

                {/* Active Package Status */}
                {!subscriptionLoading && ActivePackage && (
                    <div className={styles['active-package-banner']}>
                        <div className={styles['banner-content']}>
                            <div className={styles['banner-icon']}><FiCheck /></div>
                            <div className={styles['banner-text']}>
                                <strong>
                                    {Userpackage === 500 || Userpackage === 5000
                                        ? 'স্টার্টআপ প্যাকেজ'
                                        : Userpackage === 1000 || Userpackage === 10000
                                        ? 'স্মল বিজনেস প্যাকেজ'
                                        : Userpackage === 4000 || Userpackage === 40000
                                        ? 'বিজনেস প্যাকেজ'
                                        : 'কাস্টম প্যাকেজ'
                                    } (৳{Userpackage})
                                </strong>
                                <span>Next billing: {nextBilling}</span>
                                <span>
                                    Visits remaining:{' '}
                                    {Unlimited === '1' || VisitorLeft === -1
                                        ? 'Unlimited'
                                        : (VisitorLeft || 0).toLocaleString()}
                                </span>
                                {orderUsage && (
                                    <>
                                        <span>
                                            অর্ডার এই মাসে:{' '}
                                            {orderUsage.limit === -1
                                                ? `${(orderUsage.count || 0).toLocaleString()} (আনলিমিটেড)`
                                                : `${(orderUsage.count || 0).toLocaleString()} / ${(orderUsage.limit || 0).toLocaleString()}`}
                                        </span>
                                        {orderUsage.extraOrders > 0 && (
                                            <span>
                                                এক্সট্রা অর্ডার (এই মাসে): {orderUsage.extraOrders.toLocaleString()} × ৳{orderUsage.rate} = ৳{orderUsage.extraThisMonth.toLocaleString()}
                                            </span>
                                        )}
                                        {orderUsage.due > 0 && (
                                            <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                                                বকেয়া এক্সট্রা অর্ডার চার্জ: ৳{orderUsage.due.toLocaleString()} (পরবর্তী বিলে যুক্ত হবে)
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Billing Toggle */}
                <div className={styles['billing-toggle-container']}>
                    <div className={styles['billing-toggle']}>
                        <span className={isMonthly ? styles['toggle-active'] : ''}>
                            Monthly
                        </span>
                        <div className={styles['toggle-switch']} onClick={handleSwitchChange}>
                            <div className={`${styles['toggle-slider']} ${!isMonthly ? styles['toggle-slider-active'] : ''}`}></div>
                        </div>
                        <span className={!isMonthly ? styles['toggle-active'] : ''}>
                            Yearly
                        </span>
                        <div className={styles['savings-badge']}>Save 17%</div>
                    </div>
                </div>

                {/* Pricing Cards */}
                {loading ? (
                    <div className={styles['skeletonPricingContainer']}>
                        {[1, 2, 3].map((i) => (
                            <div key={`skel-${i}`} className={styles['skeletonCard']}>
                                <div className={styles['skeletonCardHeader']}>
                                    <div className={styles.skeleton} style={{ width: '72px', height: '26px', borderRadius: '999px', margin: '0 auto 14px' }}></div>
                                    <div className={styles.skeleton} style={{ width: '170px', height: '22px', margin: '0 auto 10px' }}></div>
                                    <div className={styles.skeleton} style={{ width: '120px', height: '14px', margin: '0 auto 16px' }}></div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
                                        <div className={styles.skeleton} style={{ width: '16px', height: '20px' }}></div>
                                        <div className={styles.skeleton} style={{ width: '90px', height: '42px' }}></div>
                                        <div className={styles.skeleton} style={{ width: '34px', height: '16px' }}></div>
                                    </div>
                                </div>
                                <div className={styles.skeleton} style={{ width: '100%', height: '46px', borderRadius: '12px', marginBottom: '20px' }}></div>
                                <div className={styles['skeletonFeatures']}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                                        <div key={j} className={styles['skeletonFeatureRow']}>
                                            <div className={styles.skeleton} style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0 }}></div>
                                            <div className={styles.skeleton} style={{ width: `${58 + ((j * 7) % 28)}%`, height: '14px' }}></div>
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.skeleton} style={{ width: '120px', height: '15px', margin: '4px auto 0' }}></div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles['pricing-container']}>
                        {packages.map((pkg, index) => (
                            <div
                                key={pkg.id || index}
                                className={`${styles['pricing-card']} ${
                                    pkg.isPopular
                                        ? `${styles['pricing-card-premium']} ${styles['pricing-card-popular']}`
                                        : styles['pricing-card-basic']
                                }`}
                                onClick={() => handlePackageSelect(pkg)}
                            >
                                {pkg.isPopular && (
                                    <div className={styles['popular-badge']}>
                                        Most Popular
                                    </div>
                                )}
                                <div className={styles['card-header']}>
                                    <div className={styles['plan-badge']}>{pkg.badge}</div>
                                    <h3 className={styles['plan-name']}>
                                        {isMonthly ? 'মাসিক' : 'বাৎসরিক'} {pkg.name}
                                    </h3>
                                    <p className={styles['plan-tagline']}>{pkg.tagline}</p>
                                    <div className={styles['plan-price']}>
                                        <span className={styles['currency']}>৳</span>
                                        <span className={styles['amount']}>
                                            {isMonthly ? pkg.monthly.displayPrice : pkg.yearly.displayPrice}
                                        </span>
                                        <span className={styles['period']}>
                                            /{isMonthly ? pkg.monthly.period : pkg.yearly.period}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    className={`${styles['plan-button']} ${
                                        pkg.isPopular ? styles['plan-button-premium'] : styles['plan-button-basic']
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePackageSelect(pkg);
                                    }}
                                >
                                    এই প্ল্যান নিন
                                </button>

                                <ul className={styles['highlights-list']}>
                                    {pkg.highlights.map((h, hIndex) => (
                                        <li
                                            key={hIndex}
                                            className={`${styles['highlight-item']} ${
                                                h.included ? styles['hl-on'] : styles['hl-off']
                                            }`}
                                        >
                                            <span className={styles['hl-icon']}>
                                                {h.included ? <FiCheck /> : <FiX />}
                                            </span>
                                            <span>{h.text}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    type="button"
                                    className={styles['see-all']}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        document
                                            .getElementById('all-features')
                                            ?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                >
                                    সব ফিচার দেখুন <FiArrowDown />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div id="all-features">
                    <Features />
                </div>
            </div>
        </>
    );
}

export default Billing;
